import { useState, useEffect } from "react";
import { Menu, X, ChevronRight } from "lucide-react";
import BranchSelector from "./BranchSelector";

export default function Navbar({ selectedBranch, setSelectedBranch }) {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", href: "#" },
    { name: "About", href: "#about" },
    { name: "Doctors", href: "#doctors" },
    { name: "Services", href: "#services" },
    { name: "Gallery", href: "#gallery" },
    { name: "FAQ", href: "#faq" },
    { name: "Contact", href: "#contact" },
  ];

  const handleBookAppointment = () => {
    const contactSection = document.getElementById("contact");
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${
      scrolled ? "bg-white/95 backdrop-blur-md shadow-2xl" : "bg-transparent"
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24 md:h-28">
          {/* Logo */}
          <div className="flex items-center group cursor-pointer relative mt-4 md:mt-6">
            <div className="relative transform group-hover:scale-105 transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-xl"></div>
              <img 
                src="/images/olive11.png" 
                alt="Olive Dental Clinic Logo"
                className="h-16 md:h-20 w-auto object-contain rounded-lg shadow-md"
              />
            </div>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-4">
            <div className="flex space-x-1">
              {navLinks.map((link, idx) => (
                <a
                  key={idx}
                  href={link.href}
                  className="px-4 py-2 text-gray-700 font-medium relative group transition-all duration-300 hover:text-[#4a7c59]"
                >
                  {link.name}
                  <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f] group-hover:w-full group-hover:left-0 transition-all duration-300"></span>
                </a>
              ))}
            </div>
            
            {/* Branch Selector */}
            <BranchSelector 
              selectedBranch={selectedBranch} 
              setSelectedBranch={setSelectedBranch} 
            />
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <button
              onClick={handleBookAppointment}
              className="px-6 py-2.5 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f] text-white rounded-full font-semibold hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              Book Appointment
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg bg-[#4a7c59]/10 text-[#4a7c59]"
            aria-label="Menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`md:hidden fixed top-24 left-0 w-full bg-white shadow-2xl transform transition-all duration-500 ${
        isOpen ? "translate-x-0" : "-translate-x-full"
      }`}>
        <div className="flex flex-col p-6 space-y-4">
          {navLinks.map((link, idx) => (
            <a
              key={idx}
              href={link.href}
              onClick={() => setIsOpen(false)}
              className="flex items-center justify-between p-3 rounded-lg hover:bg-[#4a7c59]/10 transition-colors group"
            >
              <span className="text-gray-700 font-medium group-hover:text-[#4a7c59]">{link.name}</span>
              <ChevronRight className="w-4 h-4 text-[#4a7c59] opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          ))}
          
          {/* Mobile Branch Selector */}
          <div className="px-3 py-2">
            <BranchSelector 
              selectedBranch={selectedBranch} 
              setSelectedBranch={setSelectedBranch} 
              isMobile={true}
            />
          </div>
          
          <button
            onClick={() => {
              setIsOpen(false);
              handleBookAppointment();
            }}
            className="mt-4 px-6 py-3 bg-gradient-to-r from-[#4a7c59] to-[#2d5a3f] text-white rounded-full text-center font-semibold"
          >
            Book Appointment
          </button>
        </div>
      </div>
    </nav>
  );
}